import React, { useCallback, useState, useEffect, useRef } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { MapContainerProps } from "../../../types/restaurant";
import RouteDirectionsWithAutocomplete from "../../../components/RouteDirectionsWithAutocomplete";

// Map container styling
const containerStyle = {
  width: "100%",
  height: "100%",
  borderRadius: "20px",
};

// Dark theme map styles to match design
const darkMapStyles = [
  { elementType: "geometry", stylers: [{ color: "#212121" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#212121" }] },
  {
    featureType: "administrative",
    elementType: "geometry",
    stylers: [{ color: "#757575" }],
  },
  {
    featureType: "administrative.country",
    elementType: "labels.text.fill",
    stylers: [{ color: "#9e9e9e" }],
  },
  {
    featureType: "administrative.land_parcel",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [{ color: "#bdbdbd" }],
  },
  {
    featureType: "poi",
    elementType: "labels.text.fill",
    stylers: [{ color: "#757575" }],
  },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ color: "#181818" }],
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.fill",
    stylers: [{ color: "#616161" }],
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.stroke",
    stylers: [{ color: "#1b1b1b" }],
  },
  {
    featureType: "road",
    elementType: "geometry.fill",
    stylers: [{ color: "#2c2c2c" }],
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ color: "#8a8a8a" }],
  },
  {
    featureType: "road.arterial",
    elementType: "geometry",
    stylers: [{ color: "#373737" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#3c3c3c" }],
  },
  {
    featureType: "road.highway.controlled_access",
    elementType: "geometry",
    stylers: [{ color: "#4e4e4e" }],
  },
  {
    featureType: "road.local",
    elementType: "labels.text.fill",
    stylers: [{ color: "#616161" }],
  },
  {
    featureType: "transit",
    elementType: "labels.text.fill",
    stylers: [{ color: "#757575" }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#000000" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#3d3d3d" }],
  },
];

// Map options
const mapOptions = {
  styles: darkMapStyles,
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  scaleControl: false,
  streetViewControl: false,
  rotateControl: false,
  fullscreenControl: true,
};

const MapContainer: React.FC<MapContainerProps> = ({
  restaurants,
  userLocation,
  selectedRestaurant,
  onMarkerClick,
  center,
  zoom,
  onNearbyRestaurantsChange,
  onRouteDistancesChange,
  initialRouteDestination,
}) => {
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [nearbyRestaurantIds, setNearbyRestaurantIds] = useState<string[]>([]);
  const [routeDistances, setRouteDistances] = useState<Map<string, number>>(
    new Map()
  );
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);

  // Notify parent when nearby restaurants change
  React.useEffect(() => {
    if (onNearbyRestaurantsChange) {
      onNearbyRestaurantsChange(nearbyRestaurantIds);
    }
  }, [nearbyRestaurantIds, onNearbyRestaurantsChange]);

  // Notify parent when route distances change
  React.useEffect(() => {
    if (onRouteDistancesChange) {
      onRouteDistancesChange(routeDistances);
    }
  }, [routeDistances, onRouteDistancesChange]);

  // Create custom marker icon for restaurants (restaurant/utensils icon)
  const createRestaurantMarkerIcon = (
    isSelected: boolean,
    isNearRoute: boolean = false
  ): string => {
    // Green for near route, Red for selected, Yellow for normal
    const fillColor = isNearRoute
      ? "%2310B981"
      : isSelected
        ? "%23EF4444"
        : "%23FCD34D";

    // Create SVG icon with background circle
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="14" fill="${fillColor.replace(
      "%23",
      "#"
    )}" stroke="white" stroke-width="2"/>
      <path d="M14 12h-1V8h-1v4h-1V8h-1v4c0 1.1.83 2 1.88 2.03V20h1.25v-5.97C13.17 14 14 13.1 14 12V8h-1v4zm2.5-1v4h1.25v4H19V8c-1.38 0-2.5 1.12-2.5 2.5z" fill="white" transform="translate(4, 4)"/>
    </svg>`;

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  };

  // Create custom marker icon for user location (blue circle with pulse effect)
  const createUserLocationIcon = (): google.maps.Symbol => {
    return {
      path: google.maps.SymbolPath.CIRCLE,
      scale: 12, // Increased size
      fillColor: "#3B82F6",
      fillOpacity: 0.9,
      strokeColor: "#FFFFFF",
      strokeWeight: 4,
    };
  };

  // Handle map instance load
  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    // Small delay to ensure Google Maps API is fully initialized
    setTimeout(() => {
      setIsMapLoaded(true);
      console.log("Map loaded successfully, ready to render markers");
    }, 100);
  }, []);

  // Debug: Log user location
  React.useEffect(() => {
    console.log("User location:", userLocation);
    console.log("Map loaded:", isMapLoaded);
    console.log("Should show marker:", userLocation && isMapLoaded);
  }, [userLocation, isMapLoaded]);

  // Clean up markers on unmount
  useEffect(() => {
    return () => {
      markersRef.current.forEach((marker: any) => {
        if (marker.setMap) {
          marker.setMap(null);
        }
      });
      markersRef.current = [];
    };
  }, []);

  // Update marker clustering when restaurants change
  useEffect(() => {
    // Check if Google Maps API is loaded
    if (typeof google === "undefined" || !google.maps) {
      console.warn("Google Maps API not loaded yet");
      return;
    }

    // Ensure map is loaded and we have a valid map instance
    if (!mapRef.current || !isMapLoaded || restaurants.length === 0) {
      console.log("Waiting for map or restaurants:", {
        hasMapRef: !!mapRef.current,
        isMapLoaded,
        restaurantCount: restaurants.length,
      });
      return;
    }

    console.log("Creating markers for", restaurants.length, "restaurants");

    // Clear existing markers
    markersRef.current.forEach((marker: any) => {
      try {
        if (marker.setMap) {
          marker.setMap(null);
        }
      } catch (error) {
        console.warn("Error clearing marker:", error);
      }
    });
    markersRef.current = [];

    // Filter restaurants with valid coordinates, Active status and on highway
    let validRestaurants = restaurants.filter(
      (restaurant) =>
        restaurant.storeLat !== 0 &&
        restaurant.storeLong !== 0 &&
        restaurant.isHighway === true
    );

    // If there are nearby restaurants from route, show only those
    if (nearbyRestaurantIds.length > 0) {
      validRestaurants = validRestaurants.filter((restaurant) =>
        nearbyRestaurantIds.includes(restaurant.dbID)
      );
      console.log(
        `Showing only ${validRestaurants.length} restaurants near the route`
      );
    } else {
      console.log(
        `Creating ${validRestaurants.length} markers (filtered from ${restaurants.length})`
      );
    }

    // Create new markers with custom HTML
    const newMarkers = validRestaurants.map((restaurant) => {
      const isSelected = selectedRestaurant?.dbID === restaurant.dbID;
      const isNearRoute = nearbyRestaurantIds.includes(restaurant.dbID);

      const color = isNearRoute
        ? "#10B981"
        : isSelected
          ? "#EF4444"
          : "#FCD34D";

      // Create custom HTML marker
      const markerDiv = document.createElement("div");
      markerDiv.className = "custom-restaurant-marker";
      markerDiv.style.cssText = `
        display: flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        position: absolute;
        transform: translate(-16px, -16px);
      `;

      markerDiv.innerHTML = `
        <div style="
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background-color: ${color};
          border: 2px solid white;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        ">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
            <path d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z"/>
          </svg>
        </div>
        <span style="
          color: #1F2937;
          font-size: 13px;
          font-weight: 700;
          white-space: nowrap;
          background-color: white;
          padding: 4px 8px;
          border-radius: 4px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        ">${restaurant.name}</span>
      `;

      // Create overlay
      class CustomMarker extends google.maps.OverlayView {
        position: google.maps.LatLng;
        div: HTMLDivElement;

        constructor(position: google.maps.LatLng, div: HTMLDivElement) {
          super();
          this.position = position;
          this.div = div;
        }

        onAdd() {
          const panes = this.getPanes();
          panes?.overlayMouseTarget.appendChild(this.div);
        }

        draw() {
          const projection = this.getProjection();
          const point = projection.fromLatLngToDivPixel(this.position);
          if (point) {
            this.div.style.left = point.x + "px";
            this.div.style.top = point.y + "px";
          }
        }

        onRemove() {
          if (this.div.parentNode) {
            this.div.parentNode.removeChild(this.div);
          }
        }
      }

      const position = new google.maps.LatLng(
        restaurant.storeLat,
        restaurant.storeLong
      );
      const overlay = new CustomMarker(position, markerDiv);
      overlay.setMap(mapRef.current!);

      // Add click listener
      markerDiv.addEventListener("click", () => {
        console.log("Marker clicked:", restaurant.name);
        onMarkerClick(restaurant);
      });

      return overlay as any;
    });

    console.log(`Successfully created ${newMarkers.length} markers`);
    markersRef.current = newMarkers;
  }, [
    restaurants,
    selectedRestaurant,
    isMapLoaded,
    onMarkerClick,
    nearbyRestaurantIds,
  ]);

  console.log("User location: started rendering", userLocation);
  console.log("Map loaded: started rendering", isMapLoaded);
  console.log("restorants: started rendering", restaurants);

  return (
    <div
      className=" relative rounded-xl mt-4 w-[calc(100vw-2rem)] h-[calc(100vh-6.5rem)]"
      role="application"
      aria-label="Interactive restaurant map"
    >
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={zoom}
        options={{
          // Note: Cannot use custom styles when mapId is present
          // Styles must be configured in Google Cloud Console for the Map ID
          disableDefaultUI: false,
          cameraControl: false,
          zoomControl: false,
          mapTypeControl: false,
          scaleControl: false,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: false,
          // Enable keyboard navigation
          keyboardShortcuts: true,
          // Ensure map is accessible
          gestureHandling: "greedy",
          // Map ID is now required even for deprecated Marker API
          mapId:
            process.env.REACT_APP_GOOGLE_MAP_ID || "c20dacceaeabfb2c718d014c",
        }}
        onLoad={onMapLoad}
      >
        {/* User location marker (blue) - managed by React component */}
        {userLocation && isMapLoaded && (
          <>
            <Marker
              position={{
                lat: userLocation.latitude,
                lng: userLocation.longitude,
              }}
              icon={createUserLocationIcon()}
              title="Your Location"
              zIndex={1000}
            // key={}
            />
            {/* Add a larger outer circle for better visibility */}
            <Marker
              position={{
                lat: userLocation.latitude,
                lng: userLocation.longitude,
              }}
              icon={{
                path: google.maps.SymbolPath.CIRCLE,
                scale: 20,
                fillColor: "#3B82F6",
                fillOpacity: 0.2,
                strokeColor: "#3B82F6",
                strokeWeight: 1,
              }}
              zIndex={999}
            />
          </>
        )}

        {/* Restaurant markers are managed via useEffect with clustering */}

        {/* Route Directions Component with Autocomplete */}
        {isMapLoaded && (
          <RouteDirectionsWithAutocomplete
            map={mapRef.current}
            restaurants={restaurants}
            onNearbyRestaurantsFound={setNearbyRestaurantIds}
            onRouteDistancesCalculated={setRouteDistances}
            initialRouteDestination={initialRouteDestination}
            userLocation={userLocation}
          />
        )}
      </GoogleMap>

      {/* Loading overlay while map initializes */}
      {!isMapLoaded && (
        <div
          className="absolute inset-0 bg-[#1a1a1a] flex items-center justify-center"
          role="status"
          aria-live="polite"
          aria-label="Initializing map"
        >
          <div className="text-center">
            <div className="relative h-16 w-16 mx-auto mb-4" aria-hidden="true">
              <div className="absolute inset-0 rounded-full border-4 border-gray-700 opacity-25"></div>
              <div className="absolute inset-0 animate-spin rounded-full border-4 border-[#6366f1] border-t-transparent"></div>
            </div>
            <p className="text-white text-lg font-medium">
              Initializing map...
            </p>
          </div>
        </div>
      )}

      {/* Legend indicator at the bottom - only show when route is selected */}
      {isMapLoaded && nearbyRestaurantIds.length > 0 && (
        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-white rounded-lg shadow-lg px-3 py-2 z-10 max-w-[80vw] w-full animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
              />
            </svg>
            <span className="text-sm font-semibold text-gray-800">
              Showing {nearbyRestaurantIds.length} restaurants near route
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapContainer;
